import React, { useMemo } from "react";
import theme from "../../../styles/theme";

/* ============================================================================ 
   COSTING PANEL — CORELIA 
   ----------------------------------------------------------------------------
   PANNEAU DE SYNTHÈSE GLOBALE POUR L'ONGLET COSTING
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

// Définir les composants manquants

// Composant pour afficher un message d'état vide
const EmptyState = () => (
  <div style={S.empty}>
    <div style={S.panelHeader}>
      <div style={S.panelTitle}>Costing Overview</div>
    </div>

    <PanelBlock title="Costing snapshot">
      <div style={S.emptyText}>
        Aucune donnée disponible pour l'affichage de l'onglet Costing.
      </div>
    </PanelBlock>
  </div>
);

// Composant de bloc avec titre et contenu
const PanelBlock = ({ title, children }) => (
  <div style={S.block}>
    <div style={S.blockTitle}>{title}</div>
    {children}
  </div>
);

// Composant pour afficher une ligne clé/valeur
const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div style={S.kvRow}>
    <div style={S.kvLabel}>{label}</div>
    <div style={{ ...S.kvValue, color: valueColor }}>{value}</div>
  </div>
);

const S = {
  panel: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 12,
    minWidth: 0,
    flex: "0 0 auto",
    boxSizing: "border-box",
  },

  panelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1rem", // Ajusté pour correspondre aux standards Corelia
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
  },

  block: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingBottom: 2,
    minWidth: 0,
    flex: "0 0 auto",
    boxSizing: "border-box",
  },

  blockTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.85rem", // Harmonisé avec QuantitiesPanel
    lineHeight: 1.12,
    color: theme?.colors?.text?.caneva || "#c24c2c",
    fontWeight: 600,
  },

  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
  },

  kvRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  kvLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem", // Ajusté pour correspondre aux autres labels
    lineHeight: 1.2,
    color: T.soft,
    minWidth: 0,
  },

  kvValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem", // Harmonisé avec le label
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  empty: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  emptyText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.78rem", // Ajusté pour une meilleure lisibilité
    lineHeight: 1.35,
    color: T.verySoft,
  },
};

// Données fictives pour l'exemple
const MOCK_COSTING_DATA = {
  id: "cost-01",
  name: "Costing EPC water infrastructure",
  lineCount: 38,
  definedCount: 29,
  missingCount: 9,
  completionRate: 76,
  manualCount: 12,
  derivedCount: 10,
  parentCount: 11,
  fixedCount: 5,
  inconsistentUnits: 2,
  missingBases: 3,
  atypicalRatios: 2,
  brokenParents: 1,
  readyForReview: 24,
  pendingForReview: 14,
};

const getSignalColor = (tone) => {
  switch (tone) {
    case "green":
      return T.greenText;
    case "yellow":
      return T.yellowText;
    case "orange":
      return T.orangeText;
    case "red":
      return T.redText;
    default:
      return T.greyText;
  }
};

const getCostingReadiness = (data) => {
  const completion = data?.completionRate || 0;
  const missing = data?.missingCount || 0;
  const inconsistentUnits = data?.inconsistentUnits || 0;
  const brokenParents = data?.brokenParents || 0;

  if (completion >= 90 && missing === 0 && inconsistentUnits === 0 && brokenParents === 0) {
    return { label: "Ready for Review", tone: "green" };
  }

  if (completion >= 75) {
    return { label: "Partially ready", tone: "yellow" };
  }

  if (completion > 0) {
    return { label: "Costing in progress", tone: "orange" };
  }

  return { label: "Not defined", tone: "grey" };
};

const CostingPanel = ({ selectedData = null, useMock = true }) => {
  const data = selectedData || (useMock ? MOCK_COSTING_DATA : null);

  const metrics = useMemo(() => {
    if (!data) return null;
    return getCostingReadiness(data);
  }, [data]);

  if (!data || !metrics) return <EmptyState />;

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={S.panelTitle}>Costing Overview</div>
      </div>

      <PanelBlock title="Costing snapshot">
        <div style={S.rows}>
          <KeyValueRow
            label="Cost lines"
            value={data.lineCount ?? "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Defined"
            value={data.definedCount ?? 0}
            valueColor={T.greenText}
          />
          <KeyValueRow
            label="Missing"
            value={data.missingCount ?? 0}
            valueColor={T.orangeText}
          />
          <KeyValueRow
            label="Completion"
            value={`${data.completionRate || 0}%`}
            valueColor={data.completionRate >= 75 ? T.greenText : T.yellowText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Driver distribution">
        <div style={S.rows}>
          <KeyValueRow
            label="Manual"
            value={data.manualCount ?? 0}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Derived"
            value={data.derivedCount ?? 0}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="From parent"
            value={data.parentCount ?? 0}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Fixed"
            value={data.fixedCount ?? 0}
            valueColor={T.blueText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Consistency check">
        <div style={S.rows}>
          <KeyValueRow
            label="Inconsistent units"
            value={data.inconsistentUnits ?? 0}
            valueColor={(data.inconsistentUnits || 0) > 0 ? T.orangeText : T.greenText}
          />
          <KeyValueRow
            label="Missing bases"
            value={data.missingBases ?? 0}
            valueColor={(data.missingBases || 0) > 0 ? T.orangeText : T.greenText}
          />
          <KeyValueRow
            label="Atypical ratios"
            value={data.atypicalRatios ?? 0}
            valueColor={(data.atypicalRatios || 0) > 0 ? T.yellowText : T.greenText}
          />
          <KeyValueRow
            label="Broken parent links"
            value={data.brokenParents ?? 0}
            valueColor={(data.brokenParents || 0) > 0 ? T.redText : T.greenText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Readiness for Review">
        <div style={S.rows}>
          <KeyValueRow
            label="Ready"
            value={data.readyForReview ?? 0}
            valueColor={T.greenText}
          />
          <KeyValueRow
            label="To complete"
            value={data.pendingForReview ?? 0}
            valueColor={T.orangeText}
          />
          <KeyValueRow
            label="Global readiness"
            value={metrics.label}
            valueColor={getSignalColor(metrics.tone)}
          />
        </div>
      </PanelBlock>
    </div>
  );
};

export default CostingPanel;